import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkout } from '../context/WorkoutContext';
import { muscleGroupNames } from '../data/exerciseData';
import { generateWorkout } from '../data/exerciseData';
import UserDetailsModal from '../components/UserDetailsModal';
import './WorkoutSummary.css';

// Simple email service for your existing EmailJS setup
const sendWorkoutEmail = async (userData, workoutData, trainerInfo = null) => {
  if (!window.emailjs) {
    throw new Error('EmailJS not loaded');
  }

  // Initialize EmailJS if not already done
  if (!window.emailjs._initialized) {
    window.emailjs.init('Ke_xwVYFVvuHuFKnX'); // Your public key
    window.emailjs._initialized = true;
  }

  const workoutDate = (() => {
    try {
      const now = new Date();
      // Try modern formatting first
      try {
        return now.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric'
        });
      } catch (localeError) {
        // Fallback to basic formatting
        try {
          return now.toLocaleString();
        } catch (basicError) {
          // Manual formatting fallback
          const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
          const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

          const year = now.getFullYear();
          const month = months[now.getMonth()];
          const day = days[now.getDay()];
          const date = now.getDate();
          const hours = now.getHours();
          const minutes = now.getMinutes().toString().padStart(2, '0');
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;

          return `${day}, ${month} ${date}, ${year} ${displayHours}:${minutes} ${ampm}`;
        }
      }
    } catch (error) {
      return new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
    }
  })();

  // Create workout summary for your existing template
  let workoutSummary;
  if (trainerInfo?.isTrainerProgram) {
    workoutSummary = `🏋️ WORKOUT COMPLETE! 🏋️

"${trainerInfo.programName}" by ${trainerInfo.trainerName}

📊 WORKOUT STATS:
• Intensity: ${trainerInfo.intensityLabel} (${trainerInfo.intensityLevel}%)
• Exercises: ${workoutData.exercises.length} total exercises  
• Reps per Exercise: ${workoutData.repCount}
• Total Reps: ${workoutData.exercises.length * workoutData.repCount}
• Date: ${workoutDate}

💪 EXERCISES:
${workoutData.exercises.map((exercise, index) => `${index + 1}. ${exercise} - ${workoutData.repCount} reps`).join('\n')}

────────────────────────────────
🔥 BUMFIT - Train - Build - Battle 🔥
bumfit.com - Where every word is a workout!
Unlimited workouts at your fingertips.
────────────────────────────────`;
  } else {
    workoutSummary = `🏋️ YOUR BUMFIT WORKOUT! 🏋️

💪 CUSTOM WORKOUT

📊 WORKOUT STATS:
• Total Exercises: ${workoutData.exercises.length}
• Reps per Exercise: ${workoutData.repCount}
• Sets: ${workoutData.setCount}
• Total Reps: ${workoutData.exercises.length * workoutData.repCount * workoutData.setCount}
• Muscle Groups: ${workoutData.selectedMuscleGroups}
• Date: ${workoutDate}

💪 EXERCISES:
${workoutData.exercises.map((exercise, index) => `${index + 1}. ${exercise} - ${workoutData.repCount} reps`).join('\n')}

────────────────────────────────
🔥 BUMFIT - Train - Build - Battle 🔥
bumfit.com - Where every word is a workout!
Unlimited workouts at your fingertips.
────────────────────────────────`;
  }

  // Email parameters for your existing template
  const emailParams = {
    to_email: userData.email,
    workout_title: trainerInfo?.isTrainerProgram
        ? `🎮 ${trainerInfo.programName} by ${trainerInfo.trainerName}`
        : `🏋️ Your BUMFIT Workout`,
    exercise_count: workoutData.exercises.length,
    total_reps: workoutData.exercises.length * workoutData.repCount * (workoutData.setCount || 1),
    timestamp: workoutDate,
    workout_summary: workoutSummary,
    // Add BCC to yourself (replace with your email)
    bcc_email: 'redhubcap@gmail.com' // Replace this with your actual email
  };

  try {
    const response = await window.emailjs.send(
        'service_d5cc5ls',     // Your service ID
        'template_af7akil',    // Your existing template ID
        emailParams,
        'Ke_xwVYFVvuHuFKnX'    // Your public key
    );

    console.log('Email sent successfully:', response);
    return { success: true, response };
  } catch (error) {
    console.error('Email error:', error);
    throw new Error('Failed to send email: ' + (error.text || error.message));
  }
};

const WorkoutSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, actions, computed } = useWorkout();
  const { generatedWorkout, selectedMuscleGroups, repCount, setCount, difficultyLevel } = state;

  // Modal state
  const [showUserModal, setShowUserModal] = useState(false);
  const [isSubmittingUserData, setIsSubmittingUserData] = useState(false);
  const [userRegistered, setUserRegistered] = useState(false);
  const [userData, setUserData] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [emailInProgress, setEmailInProgress] = useState(false);

  // Get workout info from location state
  const workoutCode = location.state?.workoutCode ||
      location.state?.selectedMuscleGroups ||
      selectedMuscleGroups || '';
  const workoutType = location.state?.workoutType || 'custom';
  const locationRepCount = location.state?.repCount;
  const locationSetCount = location.state?.setCount;
  const locationDifficultyLevel = location.state?.difficultyLevel;

  // Check if this is a trainer program
  const isTrainerProgram = location.state?.isTrainerProgram || false;
  const trainerName = location.state?.trainerName;
  const programName = location.state?.programName;
  const intensityLevel = location.state?.intensityLevel;
  const intensityLabel = location.state?.intensityLabel;

  const trainerInfo = isTrainerProgram ? {
    isTrainerProgram,
    trainerName,
    programName,
    intensityLevel,
    intensityLabel
  } : null;

  // Check if user is already registered (has saved data)
  useEffect(() => {
    const savedUserData = localStorage.getItem('bumfitUserData');
    if (savedUserData) {
      try {
        const parsed = JSON.parse(savedUserData);
        if (parsed.email) {
          setUserData(parsed);
          setUserRegistered(true);
        }
      } catch (e) {
        console.error('Error loading saved user data:', e);
      }
    }
  }, []);

  // Update context with values from location state if they exist
  useEffect(() => {
    if (locationRepCount && locationRepCount !== repCount) {
      actions.setRepCount(locationRepCount);
    }
    if (locationSetCount && locationSetCount !== setCount) {
      actions.setSetCount(locationSetCount);
    }
    if (locationDifficultyLevel && locationDifficultyLevel !== difficultyLevel) {
      actions.setDifficultyLevel(locationDifficultyLevel);
    }
  }, [locationRepCount, locationSetCount, locationDifficultyLevel, repCount, setCount, difficultyLevel, actions]);

  // Show user modal if not registered and no workout generated
  useEffect(() => {
    if (!userRegistered && generatedWorkout.length === 0 && workoutCode && workoutCode.trim() !== '') {
      setShowUserModal(true);
    }
  }, [userRegistered, generatedWorkout.length, workoutCode]);

  // Generate workout if user is registered and no workout exists - COMBINED WITH EMAIL SENDING
  useEffect(() => {
    const shouldGenerate = userRegistered && generatedWorkout.length === 0 && workoutCode && workoutCode.trim() !== '';

    if (shouldGenerate) {
      console.log('Generating workout for registered user:', workoutCode);

      // Set the muscle groups first
      actions.setMuscleGroups(workoutCode);

      let finalWorkout;
      if (isTrainerProgram && (locationSetCount > 1 || setCount > 1)) {
        // For trainer programs with multiple sets
        const baseExercises = generateWorkout(workoutCode);
        const fullWorkout = [];
        const numSets = locationSetCount || setCount || 1;

        for (let setIndex = 0; setIndex < numSets; setIndex++) {
          fullWorkout.push(...baseExercises);
        }
        finalWorkout = fullWorkout;
      } else {
        // For regular workouts or single-set trainer programs
        finalWorkout = generateWorkout(workoutCode);
      }

      // Generate the workout
      actions.generateWorkout(finalWorkout);

      // Send email immediately after workout generation (only if not already sent AND user didn't skip)
      if (userData && userData.email && !userData.skipEmail && !emailSent && !emailInProgress) {
        setEmailInProgress(true);

        const sendEmail = async () => {
          try {
            const workoutData = {
              exercises: finalWorkout,
              repCount: repCount || locationRepCount || 10,
              setCount: setCount || locationSetCount || 1,
              selectedMuscleGroups: workoutCode,
              workoutType: workoutType
            };

            const trainerInfo = isTrainerProgram ? {
              isTrainerProgram,
              trainerName,
              programName,
              intensityLevel,
              intensityLabel
            } : null;

            console.log('Sending workout email...');
            await sendWorkoutEmail(userData, workoutData, trainerInfo);

            console.log('✅ Email sent successfully');
            setEmailSent(true);
          } catch (error) {
            console.error('Failed to send email:', error);
            // Don't show error to user - they already have their workout
          } finally {
            setEmailInProgress(false);
          }
        };

        // Small delay to ensure UI updates
        setTimeout(sendEmail, 500);
      }
    }
  }, [userRegistered, workoutCode, generatedWorkout.length, isTrainerProgram, locationSetCount, setCount, actions, userData, emailSent, emailInProgress, repCount, locationRepCount, workoutType, trainerName, programName, intensityLevel, intensityLabel]);

  // Redirect if no workout code available
  useEffect(() => {
    if (!workoutCode || workoutCode.trim() === '') {
      console.log('No workout code available, redirecting to home');
      navigate('/');
    }
  }, [workoutCode, navigate]);

  // Handle user data submission (updated to handle skip option)
  const handleUserDataSubmit = async (submittedUserData) => {
    setIsSubmittingUserData(true);

    try {
      // Save user data (even if they skipped email)
      setUserData(submittedUserData);
      setUserRegistered(true);
      setShowUserModal(false);

      if (submittedUserData.skipEmail) {
        console.log('User skipped email collection');
      } else {
        console.log('User registered successfully:', submittedUserData.email);
      }

      // The workout will be generated in the useEffect above

    } catch (error) {
      console.error('Error registering user:', error);
      alert('Registration failed: ' + error.message);
    } finally {
      setIsSubmittingUserData(false);
    }
  };

  // Handle skip option specifically
  const handleSkipEmail = () => {
    const skipUserData = {
      email: null,
      skipEmail: true,
      name: 'Fitness Enthusiast' // Default name for skip users
    };
    handleUserDataSubmit(skipUserData);
  };

  // Handle starting the workout
  const handleStartWorkout = () => {
    // Use current state values or fallback to location state
    const currentRepCount = repCount || locationRepCount || 10;
    const currentSetCount = setCount || locationSetCount || 1;

    // Store trainer program info in sessionStorage before navigation
    if (isTrainerProgram) {
      const trainerProgramInfo = {
        isTrainerProgram,
        trainerName,
        programName,
        intensityLevel,
        intensityLabel
      };
      sessionStorage.setItem('trainerProgramInfo', JSON.stringify(trainerProgramInfo));
    }

    // Navigate to the appropriate workout screen
    let workoutRoute;
    const encodedWorkoutCode = encodeURIComponent(workoutCode);

    switch (workoutType) {
      case 'bodyweight':
        workoutRoute = `/bodyweight-workout/${encodedWorkoutCode}/${currentRepCount}/${currentSetCount}`;
        break;
      case 'bands':
        workoutRoute = `/bands-workout/${encodedWorkoutCode}/${currentRepCount}/${currentSetCount}`;
        break;
      case 'strength':
        workoutRoute = `/strength-workout/${encodedWorkoutCode}/${currentRepCount}/${currentSetCount}`;
        break;
      case 'advanced-bodyweight':
        workoutRoute = `/advanced-bodyweight-workout/${encodedWorkoutCode}/${currentRepCount}/${currentSetCount}`;
        break;
      case 'everything':
        workoutRoute = `/everything-workout/${encodedWorkoutCode}/${currentRepCount}/${currentSetCount}`;
        break;
      case 'battle':
        workoutRoute = `/battle-workout/${encodedWorkoutCode}/${currentRepCount}/${currentSetCount}`;
        break;
      default:
        workoutRoute = `/bodyweight-workout/${encodedWorkoutCode}/${currentRepCount}/${currentSetCount}`;
    }

    navigate(workoutRoute, {
      state: {
        workoutCode,
        workoutType,
        repCount: currentRepCount,
        setCount: currentSetCount,
        generatedWorkout,
        isTrainerProgram,
        trainerName,
        programName,
        intensityLevel,
        intensityLabel
      }
    });
  };

  // Group generated exercises by muscle group
  const getExercisesByMuscleGroup = () => {
    const exercisesByMuscleGroup = {};

    if (!workoutCode || generatedWorkout.length === 0) {
      return exercisesByMuscleGroup;
    }

    if (isTrainerProgram && (locationSetCount > 1 || setCount > 1)) {
      // For trainer programs with multiple sets, group by sets
      const numSets = locationSetCount || setCount || 1;
      const basePatternLength = workoutCode.length;
      const exercisesPerSet = basePatternLength;

      for (let setIndex = 0; setIndex < numSets; setIndex++) {
        const setKey = `set-${setIndex + 1}`;
        const startIndex = setIndex * exercisesPerSet;
        const endIndex = startIndex + exercisesPerSet;

        exercisesByMuscleGroup[setKey] = {
          name: `Set ${setIndex + 1}`,
          exercises: generatedWorkout.slice(startIndex, endIndex)
        };
      }
    } else {
      // For regular workouts, group by muscle groups
      const muscleGroupsString = workoutCode;
      const muscleGroupChars = muscleGroupsString.split('');
      const exercisesPerGroup = Math.ceil(generatedWorkout.length / muscleGroupChars.length);

      muscleGroupChars.forEach((char, groupIndex) => {
        const muscleGroupName = muscleGroupNames[char] || 'Unknown';
        const startIndex = groupIndex * exercisesPerGroup;
        const endIndex = Math.min(startIndex + exercisesPerGroup, generatedWorkout.length);

        if (startIndex < generatedWorkout.length) {
          exercisesByMuscleGroup[char] = {
            name: muscleGroupName,
            exercises: generatedWorkout.slice(startIndex, endIndex)
          };
        }
      });
    }

    return exercisesByMuscleGroup;
  };

  const exercisesByMuscleGroup = getExercisesByMuscleGroup();
  const totalExercises = generatedWorkout.length;
  const currentRepCount = repCount || locationRepCount || 10;
  const currentSetCount = setCount || locationSetCount || 1;

  // Calculate total volume correctly for trainer programs vs regular workouts
  let totalVolume;
  if (isTrainerProgram && currentSetCount > 1) {
    totalVolume = currentRepCount * totalExercises;
  } else {
    totalVolume = currentRepCount * currentSetCount * totalExercises;
  }

  // Calculate unique exercises count for display purposes
  let uniqueExercisesCount;
  if (isTrainerProgram && currentSetCount > 1) {
    uniqueExercisesCount = Math.floor(totalExercises / currentSetCount);
  } else {
    uniqueExercisesCount = totalExercises;
  }

  // Format date for display
  const getCurrentDate = () => {
    const date = new Date();
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Show user registration modal
  if (showUserModal) {
    return (
        <>
          <div className="workout-summary-container">
            <div className="summary-card">
              <h2>Almost Ready!</h2>
              {isTrainerProgram && (
                  <div className="trainer-program-info">
                    <p>Preparing {programName} by {trainerName}</p>
                    {intensityLabel && (
                        <p>Intensity: {intensityLabel} ({intensityLevel}%)</p>
                    )}
                  </div>
              )}
              <p>We'll send your workout to your email for easy access!</p>
            </div>
          </div>

          <UserDetailsModal
              isOpen={showUserModal}
              onSubmit={handleUserDataSubmit}
              onCancel={() => {
                setShowUserModal(false);
                navigate('/');
              }}
              isSubmitting={isSubmittingUserData}
              workoutType={workoutType}
              trainerInfo={trainerInfo}
          />
        </>
    );
  }

  // Show loading state if workout is being generated
  if (userRegistered && workoutCode && generatedWorkout.length === 0) {
    return (
        <div className="workout-summary-container">
          <div className="summary-card">
            <h2>Generating Your Workout...</h2>
            {userData && (
                <p>Hey there! We're creating your personalized workout.</p>
            )}
            {isTrainerProgram && (
                <div className="trainer-program-info">
                  <p>Creating {programName} by {trainerName}</p>
                  {intensityLabel && (
                      <p>Intensity: {intensityLabel} ({intensityLevel}%)</p>
                  )}
                </div>
            )}
            <p>Your workout will be sent to {userData?.email}</p>
          </div>
        </div>
    );
  }

  return (
      <div className="workout-summary-container">
        <header className="summary-header">
          <h1>Workout Summary</h1>
        </header>

        <div className="summary-card">
          <h2>Get Ready For Your Workout</h2>
          {userData && (
              <p className="user-greeting">Hey there! 👋</p>
          )}
          {isTrainerProgram && (
              <div className="trainer-program-info">
                <h3>{programName}</h3>
                <p>Designed by {trainerName}</p>
                {intensityLabel && (
                    <p className="intensity-info">
                      Intensity: <strong>{intensityLabel} ({intensityLevel}%)</strong>
                    </p>
                )}
              </div>
          )}
          <p className="date">{getCurrentDate()}</p>

          {userData && emailSent && (
              <div className="delivery-confirmation">
                <p>✅ Workout sent to {userData.email}</p>
              </div>
          )}

          <div className="workout-stats">
            <div className="stat-item">
              <span className="stat-value">{isTrainerProgram && currentSetCount > 1 ? uniqueExercisesCount : totalExercises}</span>
              <span className="stat-label">{isTrainerProgram && currentSetCount > 1 ? 'Exercises per Set' : 'Total Exercises'}</span>
            </div>

            <div className="stat-item">
              <span className="stat-value">{currentRepCount}</span>
              <span className="stat-label">Reps per Exercise</span>
            </div>

            <div className="stat-item">
              <span className="stat-value">{currentSetCount}</span>
              <span className="stat-label">Sets</span>
            </div>

            <div className="stat-item">
              <span className="stat-value">{totalVolume}</span>
              <span className="stat-label">Total Reps</span>
            </div>
          </div>

          <div className="rep-level">
            <h3>Rep Level:</h3>
            <div className="level-indicator">
              {isTrainerProgram && intensityLabel ?
                  `${intensityLabel} (${intensityLevel}%)` :
                  (computed.repCountLevel || 'Custom Level')
              }
            </div>
          </div>
        </div>

        <div className="exercises-container">
          <div className="exercises-header">
            <h2>Your Workout Plan</h2>
            {isTrainerProgram && currentSetCount > 1 && (
                <p>Each set contains the same exercises. Complete all reps of each exercise before moving to the next.</p>
            )}
          </div>

          {Object.entries(exercisesByMuscleGroup).map(([groupKey, { name, exercises }]) => (
              <div key={groupKey} className="muscle-group-section">
                <h3 className="muscle-group-name">
                  {isTrainerProgram && currentSetCount > 1 ? (
                      <span className="set-header">{name}</span>
                  ) : (
                      <>
                        <span className="muscle-code">{groupKey}</span>
                        {name}
                      </>
                  )}
                </h3>

                <div className="exercises-list">
                  {exercises.map((exercise, index) => (
                      <div key={`${exercise}-${index}`} className="exercise-item">
                        <div className="exercise-name">{exercise}</div>
                        <div className="exercise-reps">{currentRepCount} reps</div>
                      </div>
                  ))}
                </div>
              </div>
          ))}
        </div>

        <button
            className="start-button"
            onClick={handleStartWorkout}
        >
          Start Workout
        </button>
      </div>
  );
};

export default WorkoutSummary;